/** Heading for ShipMarker. Prefer FUTURE/CURRENT sea track after AIS. Never PAST inbound. */

import { resolvePortLatLng, haversineKm } from '../pages/components/tracking/portCoords';
import { extractSeaLegs, splitAroundPin } from './routeGeojson';

export function bearingDeg(from, to) {
  if (!from || !to) return null;
  const lat1 = (from[0] * Math.PI) / 180;
  const lat2 = (to[0] * Math.PI) / 180;
  const dLon = ((to[1] - from[1]) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

/** First point on remaining route ≥ ~8 km ahead of pin. */
function futureAimPoint(pin, path) {
  if (!pin || !path.length) return null;
  let nearestI = 0;
  let nearestD = Infinity;
  path.forEach((pt, i) => {
    const d = haversineKm(pin, pt);
    if (d < nearestD) {
      nearestD = d;
      nearestI = i;
    }
  });
  const MIN_KM = 8;
  for (let i = nearestI; i < path.length; i++) {
    if (haversineKm(pin, path[i]) >= MIN_KM) return path[i];
  }
  return null;
}

function cargoPodAim(shipment, pin) {
  const stops = Array.isArray(shipment?.voyage_stops) ? shipment.voyage_stops : [];
  const names = [
    ...stops.map((s) => s?.arrival_port),
    shipment?.arrival_port,
  ].filter(Boolean);
  for (const name of names) {
    const pos = resolvePortLatLng(name);
    if (pos && haversineKm(pin, pos) > 40) return pos;
  }
  return null;
}

function angDiff(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Degrees clockwise from north. Tangent of the polyline drawn through the pin. */
export function shipmentHeading(shipment, pin) {
  if (!pin || !Number.isFinite(pin[0]) || !Number.isFinite(pin[1])) return 0;
  const pod = cargoPodAim(shipment, pin);
  const legs = extractSeaLegs(shipment?.route_geojson);
  const { traveled, remaining } = splitAroundPin(legs, pin, pod);
  const solid = traveled.length >= 2
    ? bearingDeg(traveled[traveled.length - 2], pin)
    : null;
  let dash = null;
  if (remaining.length >= 2) {
    const aim = futureAimPoint(pin, remaining) || remaining[1];
    dash = bearingDeg(pin, aim);
  }
  if (solid != null && dash != null) {
    return angDiff(solid, dash) < 60 ? dash : solid;
  }
  return dash ?? solid ?? 0;
}
